import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { doc, setDoc } from "firebase/firestore";
import { getFirebaseClient, useCrypto, useAuth, InviteLinkBox } from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import {
  pharmacyAdminApi,
  pharmacyAdminBookingsApi,
  type MembershipRow,
  type InviteRow,
} from "@/lib/callables";
import { useToast } from "@/lib/toast";
import { Link } from "wouter";

type PharmacistItem =
  | { kind: "member"; id: string; email: string; displayName: string; status: "active" | "suspended"; data: MembershipRow }
  | { kind: "invite"; id: string; email: string; displayName: string; status: "invited"; data: InviteRow };

export function itemRoutePath(item: Pick<PharmacistItem, "kind" | "id">) {
  return `/pharmacists/${item.kind === "member" ? "member" : "invite"}:${encodeURIComponent(item.id)}`;
}

function statusChip(status: PharmacistItem["status"]) {
  if (status === "active") return <Chip label="Active" size="small" color="success" variant="outlined" />;
  if (status === "suspended") return <Chip label="Suspended" size="small" color="warning" />;
  return <Chip label="Invited" size="small" color="info" variant="outlined" />;
}

function InvitePharmacistForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: (email: string, inviteLink: string, emailSent: boolean) => void;
  onCancel: () => void;
}) {
  const { showError } = useToast();
  const { status: cryptoStatus, cryptoRecoveryReason, isReady, reinit, requestRecovery, wrapChainKeyForInvite } = useCrypto();
  const { db } = getFirebaseClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [psiNumber, setPsiNumber] = useState("");
  const psiValid = /^[0-9]{5,6}$/.test(psiNumber.trim());

  const m = useMutation({
    mutationFn: async () => {
      const res = await pharmacyAdminApi.issuePharmacistInvite({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        psiNumber: psiNumber.trim(),
      });
      // Wrap the chain key under the invite token (passphrase AES-KDF) and
      // persist it on the invite doc. The mobile accept-invite screen reads
      // this and calls storeChainKeyFromToken(chainId, encryptedChainKey, token).
      const encryptedChainKey = await wrapChainKeyForInvite(res.token);
      await setDoc(
        doc(db, "invites", res.inviteId),
        { encryptedChainKey },
        { merge: true },
      );
      return res;
    },
    onSuccess: (res) => {
      setEmail("");
      setDisplayName("");
      onSuccess(res.email, res.inviteLink, res.emailSent);
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const canSubmit =
    email.trim() && displayName.trim() && psiValid && !m.isPending && isReady;

  const cryptoAlert =
    cryptoStatus === "loading" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Loading encryption key…
      </Alert>
    ) : cryptoStatus === "awaiting-delivery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Setting up encryption key — this can take up to 60 s…
      </Alert>
    ) : cryptoStatus === "escrow-recovery" ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
        Requesting automatic key recovery…
      </Alert>
    ) : cryptoStatus === "new-device" ? (
      cryptoRecoveryReason === "escrow-missing" ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Recovery unavailable — ask a chain admin who is currently signed in
          to open this pharmacy in the Chain Admin panel and click "Re-deliver
          key", then refresh this page.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void requestRecovery()}>
              Retry Recovery
            </Button>
          }
        >
          Your encryption key could not be recovered automatically. Click Retry
          Recovery to try again, or ask your Katana admin to re-deliver the key.
        </Alert>
      )
    ) : cryptoStatus === "error" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        Encryption setup failed. Click Retry to try again.
      </Alert>
    ) : cryptoStatus === "no-chain-key" ? (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reinit()}>
            Retry
          </Button>
        }
      >
        No chain key found. Click Retry, or contact a Katana admin.
      </Alert>
    ) : null;

  return (
    <Paper sx={{ p: 3, position: "relative" }}>
      <IconButton size="small" sx={{ position: "absolute", top: 8, right: 8 }} onClick={onCancel}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Add pharmacist
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <TextField
          label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Full name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="PSI number"
          value={psiNumber}
          onChange={(e) => setPsiNumber(e.target.value)}
          size="small"
          fullWidth
          error={psiNumber.trim().length > 0 && !psiValid}
          helperText={
            psiNumber.trim().length > 0 && !psiValid
              ? "Enter a 5 or 6 digit PSI registration number."
              : "5 or 6 digit PSI registration number"
          }
        />
      </Box>
      {cryptoAlert}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => m.mutate()} disabled={!canSubmit}>
          {m.isPending ? "Creating invite…" : "Add pharmacist"}
        </Button>
      </Box>
    </Paper>
  );
}

export default function PharmacistsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [inviteResult, setInviteResult] = useState<
    { email: string; inviteLink: string; emailSent: boolean } | null
  >(null);

  const membersQ = useQuery({
    queryKey: ["pharmacy-admin", "members", "all"],
    queryFn: () => pharmacyAdminApi.listPharmacists({ status: "all" }),
  });

  const invitesQ = useQuery({
    queryKey: ["pharmacy-admin", "invites"],
    queryFn: () => pharmacyAdminApi.listInvites(),
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["pharmacy-admin", "members"] });
    void qc.invalidateQueries({ queryKey: ["pharmacy-admin", "invites"] });
  };

  const isLoading = membersQ.isLoading || invitesQ.isLoading;
  const error = membersQ.error || invitesQ.error;

  const items: PharmacistItem[] = [];
  if (membersQ.data) {
    for (const m of membersQ.data.items) {
      items.push({
        kind: "member",
        id: m.membershipId,
        email: m.email,
        displayName: m.displayName,
        status: m.status === "suspended" ? "suspended" : "active",
        data: m,
      });
    }
  }
  if (invitesQ.data) {
    for (const inv of invitesQ.data.items) {
      items.push({
        kind: "invite",
        id: inv.id,
        email: inv.email,
        displayName: inv.displayName,
        status: "invited",
        data: inv,
      });
    }
  }

  const statusRank = { active: 0, invited: 1, suspended: 2 } as const;
  items.sort((a, b) => {
    const r = statusRank[a.status] - statusRank[b.status];
    if (r !== 0) return r;
    return (a.email || "").localeCompare(b.email || "");
  });

  return (
    <>
      <PageHeader
        title="Pharmacists"
        description="Manage everyone at your pharmacy — active staff, suspended accounts, and pending invites."
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setShowForm((v) => !v);
              setInviteResult(null);
            }}
          >
            {showForm ? "Cancel" : "Add pharmacist"}
          </Button>
        }
      />
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        {showForm ? (
          <InvitePharmacistForm
            onCancel={() => setShowForm(false)}
            onSuccess={(email, inviteLink, emailSent) => {
              setShowForm(false);
              setInviteResult({ email, inviteLink, emailSent });
              invalidateAll();
            }}
          />
        ) : null}

        {inviteResult ? (
          <Paper sx={{ p: 3, position: "relative" }}>
            <IconButton
              size="small"
              aria-label="Dismiss"
              sx={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setInviteResult(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <InviteLinkBox
              link={inviteResult.inviteLink}
              email={inviteResult.email}
              emailSent={inviteResult.emailSent}
            />
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button variant="outlined" size="small" onClick={() => setInviteResult(null)}>
                Done
              </Button>
            </Box>
          </Paper>
        ) : null}

        <BookingsSummaryWidget />
        <Paper>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              All pharmacists{items.length > 0 ? ` (${items.length})` : ""}
            </Typography>
          </Box>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading…</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">{(error as Error).message}</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No pharmacists yet.</Typography>
            </Box>
          ) : (
            items.map((item, i) => (
              <Box key={`${item.kind}:${item.id}`}>
                <Box
                  onClick={() => setLocation(itemRoutePath(item))}
                  sx={{
                    px: 2,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {item.email || "(no email)"}
                      </Typography>
                      {item.displayName ? (
                        <Typography variant="caption" color="text.secondary" component="p">
                          {item.displayName}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    {statusChip(item.status)}
                    <ChevronRightIcon fontSize="small" color="disabled" />
                  </Stack>
                </Box>
                {i < items.length - 1 && <Divider />}
              </Box>
            ))
          )}
        </Paper>
      </Box>
    </>
  );
}

function BookingsSummaryWidget() {
  const pharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies"],
    queryFn: () => pharmacyAdminBookingsApi.listBookingPharmacies(),
  });

  const pharmacies = pharmaciesQ.data?.items || [];
  const bookingPharmacyId = pharmacies[0]?.id;

  const today = new Date().toISOString().split("T")[0];

  const bookingsQ = useQuery({
    queryKey: ["bookings", "summary", bookingPharmacyId],
    queryFn: () =>
      pharmacyAdminBookingsApi.listBookings({
        bookingPharmacyId: bookingPharmacyId!,
        startDate: today,
      }),
    enabled: !!bookingPharmacyId,
  });

  if (pharmaciesQ.isLoading) return null;

  if (pharmacies.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary">
            Bookings
          </Typography>
          <Typography variant="h6" sx={{ my: 1 }}>
            Not set up yet
          </Typography>
          <Button component={Link} to="/bookings" variant="text" size="small" sx={{ p: 0 }}>
            Go to Bookings setup
          </Button>
        </CardContent>
      </Card>
    );
  }

  const upcomingCount =
    bookingsQ.data?.items.filter((b) => b.status === "confirmed" || b.status === "pending")
      .length ?? 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          Upcoming Bookings
        </Typography>
        <Typography variant="h4" sx={{ my: 1, fontWeight: 600 }}>
          {bookingsQ.isLoading ? "..." : upcomingCount}
        </Typography>
        <Button component={Link} to="/bookings" variant="text" size="small" sx={{ p: 0 }}>
          Manage bookings
        </Button>
      </CardContent>
    </Card>
  );
}

function MemberRow({
  member,
  onChange,
}: {
  member: MembershipRow;
  onChange: () => void;
}) {
  const { user } = useAuth();
  const isSelf = !!user && member.uid === user.uid;
  const { showSuccess, showError, showWarning } = useToast();
  const [confirming, setConfirming] = useState<"revoke" | "suspend" | "reactivate" | null>(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [editName, setEditName] = useState(member.displayName);
  const [editPsi, setEditPsi] = useState("");
  const isSuspended = member.status === "suspended";

  const updateM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.updatePharmacist({
        membershipId: member.membershipId,
        displayName: editName.trim() || undefined,
        psiNumber: editPsi.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Updated ${member.email}`);
      setEditing(false);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const revokeM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.revokeMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
        disableAuth: true,
      }),
    onSuccess: (res) => {
      showSuccess(`Revoked ${member.email}`);
      if (res.authError) showWarning(`Auth disable: ${res.authError}`);
      setConfirming(null);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const suspendM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.suspendMembership({
        membershipId: member.membershipId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      showSuccess(`Suspended ${member.email}`);
      setConfirming(null);
      setReason("");
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const reactivateM = useMutation({
    mutationFn: () =>
      pharmacyAdminApi.reactivateMembership({ membershipId: member.membershipId }),
    onSuccess: () => {
      showSuccess(`Reactivated ${member.email}`);
      onChange();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {member.email || "(no email)"}
            </Typography>
            {isSuspended && <Chip label="suspended" size="small" color="warning" />}
          </Stack>
          {member.displayName ? (
            <Typography variant="caption" color="text.secondary" component="p">
              {member.displayName}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {member.uid}
          </Typography>
          {isSuspended && member.suspendedReason ? (
            <Typography variant="caption" color="text.secondary" component="p">
              Reason: {member.suspendedReason}
            </Typography>
          ) : null}
        </Box>
        {!confirming && !editing ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Tooltip title="Edit details">
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(true);
                  setEditName(member.displayName);
                  setEditPsi("");
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isSelf ? (
              isSuspended ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setConfirming("reactivate")}
                  disabled={reactivateM.isPending}
                >
                  Reactivate
                </Button>
              ) : (
                <Button size="small" variant="outlined" onClick={() => setConfirming("suspend")}>
                  Suspend
                </Button>
              )
            ) : null}
            <Button size="small" variant="outlined" onClick={() => setConfirming("revoke")}>
              Revoke
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {editing ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "primary.light",
            bgcolor: "primary.50",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Full name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="PSI number (leave blank to keep)"
                helperText="5 or 6 digits"
                value={editPsi}
                onChange={(e) => setEditPsi(e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => updateM.mutate()}
                disabled={updateM.isPending || (!editName.trim() && !editPsi.trim())}
              >
                {updateM.isPending ? "Saving…" : "Save changes"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {confirming === "reactivate" ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "primary.light",
            bgcolor: "primary.50",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Reactivate <strong>{member.email}</strong>? They will regain access to this pharmacy.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" size="small" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                reactivateM.mutate();
                setConfirming(null);
              }}
              disabled={reactivateM.isPending}
            >
              {reactivateM.isPending ? "Reactivating…" : "Confirm reactivate"}
            </Button>
          </Stack>
        </Box>
      ) : confirming ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "error.light",
            bgcolor: "error.50",
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={
                confirming === "revoke"
                  ? "Reason (recorded permanently)"
                  : "Reason (recorded on membership)"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder={
                confirming === "revoke" ? "e.g. Left the pharmacy" : "e.g. On extended leave"
              }
              size="small"
              fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => (confirming === "revoke" ? revokeM.mutate() : suspendM.mutate())}
                disabled={revokeM.isPending || suspendM.isPending}
              >
                {confirming === "revoke"
                  ? revokeM.isPending
                    ? "Revoking…"
                    : "Confirm revoke"
                  : suspendM.isPending
                    ? "Suspending…"
                    : "Confirm suspend"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
